import React, { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BottomSheet from './BottomSheet';
import { getAuthGlassFieldSx } from '../Auth/authGlassStyles';
import {
  CUSTOM_PROPERTY_STATUSES,
  getCustomPropertyStatusLabel,
} from './customPropertyStatuses';

const PLACEHOLDER = 'Set property status...';

/**
 * iOS-style picker row for partner-admin custom property status.
 */
const CustomPropertyStatusField = ({
  value = '',
  onChange,
  disabled = false,
  label = 'Property status',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fieldSx = getAuthGlassFieldSx(theme);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const selectedLabel = useMemo(
    () => (value ? getCustomPropertyStatusLabel(value) : null),
    [value]
  );

  const openPicker = (event) => {
    if (disabled) return;
    if (isMobile) {
      setSheetOpen(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const closePicker = () => {
    setAnchorEl(null);
    setSheetOpen(false);
  };

  const handleSelect = (nextValue) => {
    onChange?.(nextValue);
    closePicker();
  };

  const rowSx = {
    ...fieldSx['& .MuiOutlinedInput-root'],
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 1.75,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    textAlign: 'left',
  };

  const optionsList = (
    <List disablePadding sx={{ width: '100%' }}>
      {CUSTOM_PROPERTY_STATUSES.map(({ value: optionValue, label: optionLabel }) => {
        const selected = value === optionValue;
        return (
          <ListItemButton
            key={optionValue}
            onClick={() => handleSelect(optionValue)}
            sx={{
              py: 1.25,
              px: { xs: 0, sm: 1.5 },
              borderRadius: '12px',
              mb: 0.25,
              bgcolor: selected ? 'rgba(0,103,102,0.08)' : 'transparent',
            }}
          >
            <ListItemText
              primary={optionLabel}
              primaryTypographyProps={{
                fontSize: '0.9375rem',
                fontWeight: selected ? 600 : 400,
                color: selected ? 'rgba(0,103,102,0.95)' : 'rgba(0,0,0,0.82)',
              }}
            />
            {selected && (
              <CheckIcon sx={{ fontSize: 18, color: 'rgba(0,103,102,0.9)', ml: 1 }} />
            )}
          </ListItemButton>
        );
      })}
    </List>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        component="label"
        sx={{
          display: 'block',
          fontSize: '14px',
          fontWeight: 500,
          color: 'rgba(0,0,0,0.55)',
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <ButtonBase
        onClick={openPicker}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchorEl) || sheetOpen}
        sx={{
          width: '100%',
          borderRadius: '14px',
          display: 'block',
          textAlign: 'left',
        }}
      >
        <Box sx={rowSx}>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.35,
              color: selectedLabel ? 'rgba(0,0,0,0.82)' : 'rgba(0,0,0,0.45)',
              pr: 1,
              minWidth: 0,
            }}
            noWrap
          >
            {selectedLabel || PLACEHOLDER}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 22,
              color: 'rgba(0,0,0,0.35)',
              flexShrink: 0,
              transform: anchorEl || sheetOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        </Box>
      </ButtonBase>

      {!isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closePicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                borderRadius: '14px',
                maxHeight: 320,
                width: anchorEl ? anchorEl.getBoundingClientRect().width : undefined,
                boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
              },
            },
          }}
        >
          {CUSTOM_PROPERTY_STATUSES.map(({ value: optionValue, label: optionLabel }) => (
            <MenuItem
              key={optionValue}
              selected={value === optionValue}
              onClick={() => handleSelect(optionValue)}
              sx={{ fontSize: '0.9375rem', py: 1.1 }}
            >
              {optionLabel}
            </MenuItem>
          ))}
        </Menu>
      )}

      {isMobile && (
        <BottomSheet
          open={sheetOpen}
          onClose={closePicker}
          title="Property status"
          subtitle="Choose the current status for this listing."
        >
          {optionsList}
        </BottomSheet>
      )}
    </Box>
  );
};

export default CustomPropertyStatusField;
